/**
 * Mirrors packages/amgbaccarat_v2/lib/presentation/rive/rive_inputs.dart.
 *
 * Naming-discrepancy reference (Dart constant value ≠ Dart constant name):
 *   - MainInputs.trigPlayerWin (Dart name) → "triPlayerWin" (Rive name)
 *   - MainInputs.trigBankerWin              → "triBankerWin"
 *   - MainInputs.trigTie                    → "triTie"
 *   - MainInputs.trigGirlAnim               → "GirlAnimPlay"
 *   - MainInputs.numGirlAnim                → "GirlAnim"
 * Always trust the right-hand-side string when wiring Rive inputs.
 */

export const MainInputs = {
  stateMachine: 'Main',
  trigClearAll: 'trigClearAll',
  trigGirlAnim: 'GirlAnimPlay',
  // LEGACY (unused): the StartBet / NoMoreBet popups used to be triggers on the
  // main artboard. The designer moved them onto MainWinLose.riv's BetPanel —
  // see WinLoseInputs + RiveLayer.fireStartBet/fireNoMoreBet. Kept only to
  // document the old contract.
  trigStartBet: 'trigStartBet',
  trigStartBetIdle: 'trigStartBetIdle',
  trigNoMoreBet: 'trigNoMoreBet',
  trigClearCard: 'trigClearCard',
  // Winner triggers — live on the `top` controller, not main:
  trigPlayerWin: 'triPlayerWin',
  trigBankerWin: 'triBankerWin',
  trigTie: 'triTie',
  // Numbers
  numClearType: 'numClearType',
  numGirlAnim: 'GirlAnim',
} as const;

export const CardInputs = {
  stateMachine: 'Card',
  numPcard1: 'PlayerCard1',
  numPcard2: 'PlayerCard2',
  numPcard3: 'PlayerCard3',
  numBcard1: 'BankerCard1',
  numBcard2: 'BankerCard2',
  numBcard3: 'BankerCard3',
  /** CardPlayerBanker values: P1=1, B1=2, P2=4, B2=5, P3=3, B3=6. */
  numCardPlayerBanker: 'CardPlayerBanker',
  trigCardPlayerBanker: 'CardPlayerBankerTrigger',
  numClearCard: 'ClearCard',
  trigClearCard: 'ClearCardTrigger',
  /** Cards/scores are nested under "CardType" in the .riv. */
  nestedPath: 'CardType',
} as const;

export const BettingInputs = {
  stateMachine: 'Main',
  // Highlight triggers, one per bet area
  trigPlayer: 'trigPlayer',
  trigBanker: 'trigBanker',
  trigTie: 'trigTie',
  trigPlayerPair: 'trigPlayerPair',
  trigBankerPair: 'trigBankerPair',
  // Win-banner triggers
  trigWinPlayer: 'trigWinPlayer',
  trigWinBanker: 'trigWinBanker',
  trigWinTie: 'trigWinTie',
  // NOTE: the YOU WIN / YOU LOSE result popup is NOT here. The original Flutter
  // game drove it on this betting artboard, but this port's baccaratbettingui.riv
  // is a bet-zones-only export (~3 KB) with no YouWinLose / txtWonAmount inputs
  // and no popup graphic. The popup is driven on the MainWinLose.riv overlay
  // instead — see WinLoseInputs + RiveLayer.showWinLose.
} as const;

export const TopInputs = {
  stateMachine: 'Main',
  txtBalance: 'txtRCoinAmount',
  txtBet: 'txtBetAmount',
  txtWin: 'txtWinAmount',
  trigUpdateBalance: 'trigUpdateBalance',
} as const;

/**
 * MainWinLose.riv inputs — artboard "Button", state machine "animationManager".
 *
 * Updated contract (designer edit, Jun 2026): the StartBet / NoMoreBet popups
 * now live here and are driven by `BetPanelNumber` + `BetPanelTrigger` — set the
 * number, then fire the trigger. The old `isSoundBetPanel` boolean is no longer
 * used; audio plays from SoundManager on the `StartBet` / `NoMoreBet` Rive
 * *events* the artboard emits (see audio/soundWiring.ts).
 *
 * Verified against MainWinLose.riv via `Tools/rive introspect` and matches the
 * proven roulette_pixi / sicbo_pixi contract + legacy MainWinLoseLayer.dart.
 * This file is byte-identical (sha256 bb38ed5b…, 2 494 577 B) to
 * amg_carracing_pixi's copy, so that game's verified contract applies verbatim.
 *
 * The YOU WIN / YOU LOSE result popup ALSO lives on this overlay: set
 * `txtWonAmount` + `YouWinLose` (1 = win, 2 = lose), then fire
 * `YouWinLoseTrigger`; the artboard emits an `evtYouWinEnd` / `evtYouLoseEnd`
 * Rive event when it finishes. (The betting artboard in this port has no
 * win/lose graphic, so the popup is driven here — see RiveLayer.showWinLose.)
 */
export const WinLoseInputs = {
  stateMachine: 'animationManager',
  /** Number selecting the bet popup: 1 = StartBet, 2 = NoMoreBet. */
  betPanelNumber: 'BetPanelNumber',
  /** Fire after setting betPanelNumber to play the selected bet popup. */
  trigBetPanel: 'BetPanelTrigger',
  /** Number selecting the result popup: 1 = win, 2 = lose. */
  youWinLose: 'YouWinLose',
  /** Fire after setting youWinLose + txtWonAmount to play the result popup. */
  trigYouWinLose: 'YouWinLoseTrigger',
  /** Text run for the won/lost amount shown in the result popup. */
  txtWonAmount: 'txtWonAmount',
} as const;

/** BetPanelNumber values for the StartBet / NoMoreBet popups. */
export const BetPanelState = {
  startBet: 1,
  noMoreBet: 2,
} as const;

/**
 * Score digits on the noHIstory artboard. Each is a nested INSTANCE of the
 * reusable card/number component (the same one PlayerCard1..3 use), so the
 * digit 0..9 is written as the component's `CardType` input AT one of these
 * instance PATHS — i.e. setNumberStateAtPath('CardType', digit, 'NumberLeftTens').
 * Left = player score, Right = banker score; Tens then Units.
 */
export const ScoreInputs = {
  numLeftTens: 'NumberLeftTens',
  numLeftUnits: 'NumberLeftUnits',
  numRightTens: 'NumberRightTens',
  numRightUnits: 'NumberRightUnits',
} as const;

export const HistoryInputs = {
  /** numHistory{1..96} — 96 slots. */
  totalSlots: 96,
  nameAt(zeroBased: number): string {
    return `numHistory${zeroBased + 1}`;
  },
} as const;

/** Bet-area side identifiers used by RiveLayer's high-level API. */
export type RiveSide = 'player' | 'banker' | 'tie';
